// What a photo's tile should say about the edits attached to it.
//
// Pure and dependency-free so it can be tested directly: this decides whose
// name appears against somebody's work, and a wrong name there is worse than
// no name.
//
// Everything it needs comes from the library query, which already carries each
// sidecar's version count and editors — a tile must not fire a request per
// photo to render a badge.

// Distinct editors across every sidecar on this photo, most recent first.
// A null editor means the file arrived over SMB, which carries no identity.
// Only files a PERSON edits. A Sony clip XML and a GoPro .thm sit beside their
// media exactly like a Lightroom sidecar does, but the camera wrote them — so
// badging a video as "edited, unattributed" claims work nobody did.
const EDIT_EXT = /\.(xmp|acr)$/i;

// A sidecar that holds only what the camera recorded is not an edit.
//
// Lightroom writes an .xmp for a photograph nobody has opened: the camera
// profile, the EXIF the body recorded, and a crop covering the whole frame.
// 3,341 of the 5,652 in this library are exactly that. Badging them as edits
// says somebody worked on a photograph they never touched, which is the same
// false claim as putting the wrong name on real work.
//
// The server decides which is which and says so per sidecar; a missing answer
// is treated as an edit, because hiding somebody's work is the worse mistake.
const isCameraOnly = (sc) => sc.editingWork === false ||
  (sc.editVersions === 0 && (sc.cameraVersions || 0) > 0);

export function editSummary(allSidecars) {
  const all = (allSidecars || []).filter((sc) => EDIT_EXT.test(sc.filename || ""));
  const sidecars = all.filter((sc) => !isCameraOnly(sc));
  const cameraFiles = all.length - sidecars.length;
  if (sidecars.length === 0) {
    // Nothing anybody did — but the camera readings are still here to look at.
    return cameraFiles ? { label: null, editors: [], named: [], anyAnonymous: false,
      versions: 0, hasMasks: false, sources: [], editFiles: 0, cameraFiles } : null;
  }

  const editors = [];
  const sources = [];
  let versions = 0;
  let hasMasks = false;

  for (const sc of sidecars) {
    // A missing count means one save, not none — zero would read as "no edit".
    versions = Math.max(versions, sc.editVersions || sc.versionCount || 1);
    if (/\.acr$/i.test(sc.filename || "")) hasMasks = true;
    for (const e of sc.editors || []) {
      const who = e || null;
      if (!editors.includes(who)) editors.push(who);
    }
    for (const src of sc.sources || []) {
      if (src && !sources.includes(src)) sources.push(src);
    }
  }

  const named = editors.filter(Boolean);
  const anyAnonymous = editors.some((e) => !e);

  let label;
  if (named.length === 0) label = "unattributed";
  else if (named.length === 1) label = named[0] + (anyAnonymous ? " +1" : "");
  else label = `${named[0]} +${named.length - 1 + (anyAnonymous ? 1 : 0)}`;

  return { label, editors, named, anyAnonymous, versions, hasMasks, sources,
    editFiles: sidecars.length, cameraFiles };
}
